import { useState } from "react";
import { Plus, X } from "lucide-react";

const TYPES = [["exam", "Examen"], ["assignment", "Devoir"], ["practice", "Pratique"], ["other", "Autre"]];
const blank = (index) => ({ key: `n${index}`, subject: "", title: "", type: "assignment", dueDate: "", blocks: 2, description: "", match: null, action: "new" });

/** Flow 19 (déterministe): editable verification card. Nothing is saved before « C'est exact ». */
const CaptureReviewCard = ({ draft, mobileType = false, time, onConfirm, onCancel }) => {
  const [items, setItems] = useState(() => draft.items.map((item) => ({ ...item, dueDate: item.dueDate || "", action: item.match ? "attach" : "new" })));
  const [editing, setEditing] = useState(draft.items.some((item) => !item.dueDate && item.type !== "practice"));
  const [state, setState] = useState("open");
  const [error, setError] = useState("");
  const size = mobileType ? "text-[15px]" : "text-[13px]";
  const field = `w-full rounded-lg border border-gray-200 px-2 py-1 ${size} outline-none focus:border-[#25D366] disabled:bg-gray-50`;
  const set = (key, patch) => setItems((current) => current.map((item) => (item.key === key ? { ...item, ...patch } : item)));
  const locked = state !== "open";

  const confirm = async () => {
    const ready = items.filter((item) => item.action !== "skip");
    if (ready.some((item) => !item.title.trim())) return setError("Chaque travail a besoin d’un titre.");
    setState("saving");
    setError("");
    try {
      await onConfirm(items.map((item) => ({ ...item, dueDate: item.dueDate || null, planId: item.action === "attach" ? item.match?.planId : null })));
      setState("saved");
    } catch (problem) {
      setError(problem.message || "Enregistrement impossible.");
      setState("open");
    }
  };

  return (
    <div className="w-[92%] max-w-[24rem] self-start rounded-2xl rounded-tl-sm bg-white p-3 shadow-sm" data-testid="capture-review-card">
      <p className={`font-semibold text-[#122118] ${size}`}>À vérifier</p>
      {[...(draft.warnings || [])].map((line) => <p key={line} className="mt-1 text-[11px] text-[#8a5a00]" data-testid="capture-warning">{line}</p>)}
      {items.map((item) => (
        <div key={item.key} className={`mt-2 rounded-xl border p-2 ${item.action === "skip" ? "border-dashed border-gray-200 opacity-50" : "border-gray-100"}`} data-testid={`capture-item-${item.key}`}>
          <div className="flex gap-1.5">
            <input className={field} disabled={!editing || locked} value={item.subject} placeholder="Matière" onChange={(e) => set(item.key, { subject: e.target.value })} data-testid="capture-item-subject" />
            <select className={field} disabled={!editing || locked} value={item.type} onChange={(e) => set(item.key, { type: e.target.value })} data-testid="capture-item-type">
              {TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            {!locked && (
              <button type="button" aria-label="Retirer" onClick={() => set(item.key, { action: item.action === "skip" ? "new" : "skip" })} data-testid="capture-item-skip"><X className="h-4 w-4 text-gray-400" /></button>
            )}
          </div>
          <input className={`${field} mt-1.5`} disabled={!editing || locked} value={item.title} placeholder="Quoi" onChange={(e) => set(item.key, { title: e.target.value })} data-testid="capture-item-title" />
          <div className="mt-1.5 flex items-center gap-1.5">
            <input type="date" className={field} disabled={!editing || locked} value={item.dueDate} onChange={(e) => set(item.key, { dueDate: e.target.value })} data-testid="capture-item-date" />
            <select className={field} disabled={!editing || locked} value={item.blocks} onChange={(e) => set(item.key, { blocks: Number(e.target.value) })} data-testid="capture-item-blocks">
              {[1, 2, 3, 4, 5, 6].map((count) => <option key={count} value={count}>{count} × 20 min</option>)}
            </select>
          </div>
          {!item.dueDate && item.type !== "practice" && <p className="mt-1 text-[11px] text-[#8a5a00]">Date inconnue : ajoute-la si tu la connais.</p>}
          {item.match && item.action !== "skip" && (
            <select className={`${field} mt-1.5`} disabled={locked} value={item.action} onChange={(e) => set(item.key, { action: e.target.value })} data-testid="capture-item-match">
              <option value="attach">Ajouter au plan existant « {item.match.title} »</option>
              <option value="new">Créer un nouveau plan</option>
            </select>
          )}
        </div>
      ))}
      {error && <p className="mt-2 text-xs text-[#9b3b3b]" data-testid="capture-review-error">{error}</p>}
      {!locked && (
        <>
          <button type="button" onClick={confirm} className={`mt-3 w-full rounded-full bg-[#25D366] px-3 py-2 font-semibold text-[#0a2914] ${size}`} data-testid="capture-confirm">C’est exact</button>
          <div className="mt-2 grid grid-cols-3 gap-1.5">
            <button type="button" onClick={() => setEditing(true)} className={`rounded-full border border-gray-200 px-2 py-2 font-semibold text-[#2d5a3d] ${size}`} data-testid="capture-edit">Modifier</button>
            <button type="button" onClick={() => { setEditing(true); setItems((current) => [...current, blank(current.length + 1)]); }} className={`inline-flex items-center justify-center gap-1 rounded-full border border-gray-200 px-2 py-2 font-semibold text-[#2d5a3d] ${size}`} data-testid="capture-add"><Plus className="h-3.5 w-3.5" />Ajouter</button>
            <button type="button" onClick={() => { setState("cancelled"); onCancel(); }} className={`rounded-full border border-gray-200 px-2 py-2 font-semibold text-[#9b3b3b] ${size}`} data-testid="capture-cancel">Annuler</button>
          </div>
        </>
      )}
      {state === "saving" && <p className="mt-2 text-xs text-gray-500">Enregistrement…</p>}
      <div className="mt-1 text-right text-[10px] text-gray-500">{time}</div>
    </div>
  );
};

export default CaptureReviewCard;
